/**
 * Presenting KV-token-seconds to humans.
 *
 * The unit is correct and unfamiliar, which is a bad combination for a billing
 * page. Every number the portal shows in this unit is shown *with* a plain
 * reading of it, or every support ticket will be about what it means.
 *
 * See `docs/software.md`, "Billing follows admission": charging per output
 * token misprices this workload by roughly 20x for long-context sessions,
 * which is why the meter reads in KV-token-seconds at all.
 */

/** A typical chat context, used as the yardstick in plain-language readings. */
const TYPICAL_CONTEXT_TOKENS = 25_000;

export function formatInteger(value: number): string {
  return new Intl.NumberFormat().format(Math.round(value));
}

/** `31000` → `31.0k`, for axis labels and dense tables. */
export function formatCompact(value: number): string {
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Turn KV-token-seconds into something a person can picture.
 *
 * The arithmetic: holding a 25k-token context for one second costs 25,000
 * KV-token-seconds, so dividing by that and by 3600 gives hours of a typical
 * chat session.
 */
export function explainKvTokenSeconds(kvTokenSeconds: number): string {
  if (kvTokenSeconds <= 0) {
    return 'nothing yet';
  }

  const seconds = kvTokenSeconds / TYPICAL_CONTEXT_TOKENS;
  if (seconds < 60) {
    return `about ${Math.max(1, Math.round(seconds))}s of a 25k-context chat`;
  }
  const minutes = seconds / 60;
  if (minutes < 60) {
    return `about ${Math.round(minutes)} min of a 25k-context chat`;
  }
  const hours = minutes / 60;
  return `about ${hours < 10 ? hours.toFixed(1) : Math.round(hours)} hours of a 25k-context chat`;
}

export function formatTimestamp(value: string | null): string {
  if (!value) {
    return 'never';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'unknown';
  }
  return date.toLocaleString();
}
